/**
 * Organization service — CRUD, member management, vehicle claiming.
 * Class-based singleton per architecture rules.
 */
import { supabase } from '../supabase'
import { isDbEnum, jsonField, jsonString } from '../types/db-rows'
import type { Cols, DbEnum, Json, Row, UpdateRow } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import type {
  Organization,
  OrganizationMember,
  OrganizationPendingInvite,
  CreateOrganizationInput,
  UpdateOrganizationInput,
  InviteMemberInput,
} from '../types/organization.types'
import { formatSvNit } from '../utils/sv-nit'
import { normalizeLegalEntityName } from '../utils/sv-identity'
import { assertObjectStored, compressImage } from '../utils/image-compress'
import { uploadBytesWithSessionRecovery } from '../utils/storage-verify'

// ─── Row → camelCase mappers ────────────────────────────────────────────────

/**
 * `memberCount` / `vehicleCount` on `Organization` are computed fields that no
 * `organizations` select produces today, so the mapper leaves them unset
 * rather than pretending to read absent columns.
 */
function mapOrganization(row: Row<'organizations'>): Organization {
  return {
    id: row.id,
    name: row.name,
    legalName: row.legal_name,
    taxId: row.tax_id,
    logoUrl: row.logo_url,
    logoBlurhash: row.logo_blurhash,
    countryId: row.country_id,
    cityId: row.city_id,
    email: row.email,
    phone: row.phone,
    website: row.website,
    addressLine1: row.address_line1,
    addressLine2: row.address_line2,
    addressCity: row.address_city,
    addressRegion: row.address_region,
    addressPostalCode: row.address_postal_code,
    insuranceModel: row.insurance_model,
    allowsIndividualHosts: row.allows_individual_hosts,
    isActive: row.is_active,
    isVerified: row.is_verified,
    verifiedAt: row.verified_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/**
 * `org_member_profiles` returns JSONB, not a table, so there is no generated
 * row type to lean on. Parse defensively — the same contract as
 * `mapPendingInvite` — instead of asserting a shape the schema does not
 * promise.
 */
function mapMemberFromJson(row: Json): OrganizationMember {
  const role = jsonField(row, 'role')
  return {
    id: jsonString(jsonField(row, 'id')) ?? '',
    organizationId: jsonString(jsonField(row, 'organization_id')) ?? '',
    profileId: jsonString(jsonField(row, 'profile_id')) ?? '',
    role: isDbEnum('user_role', role) ? role : 'fleet_driver',
    permissions: Array.isArray(jsonField(row, 'permissions'))
      ? (jsonField(row, 'permissions') as string[])
      : [],
    invitedBy: jsonString(jsonField(row, 'invited_by')),
    invitedAt: jsonString(jsonField(row, 'invited_at')) ?? '',
    acceptedAt: jsonString(jsonField(row, 'accepted_at')),
    isActive: jsonField(row, 'is_active') === true,
    createdAt: jsonString(jsonField(row, 'created_at')) ?? '',
    updatedAt: jsonString(jsonField(row, 'updated_at')) ?? '',
    profile: {
      firstName: jsonString(jsonField(row, 'first_name')),
      lastName: jsonString(jsonField(row, 'last_name')),
      displayName: jsonString(jsonField(row, 'display_name')),
      avatarUrl: jsonString(jsonField(row, 'avatar_url')),
      email: jsonString(jsonField(row, 'email')) ?? '',
      phone: jsonString(jsonField(row, 'phone')),
    },
  }
}

/**
 * `list_organization_pending_invites` is declared as returning `Json`, not a
 * table, so there is no generated row type to lean on. Parse defensively
 * instead of asserting a shape the schema does not promise.
 */
function mapPendingInvite(row: Json): OrganizationPendingInvite {
  const role = jsonField(row, 'role')
  return {
    id: jsonString(jsonField(row, 'id')) ?? '',
    organizationId: jsonString(jsonField(row, 'organization_id')) ?? '',
    email: jsonString(jsonField(row, 'email')),
    phone: jsonString(jsonField(row, 'phone')),
    // Invites are always issued as fleet_driver; fall back to it rather than
    // trusting an unrecognised value out of the Json payload.
    role: isDbEnum('user_role', role) ? role : 'fleet_driver',
    invitedBy: jsonString(jsonField(row, 'invited_by')),
    createdAt: jsonString(jsonField(row, 'created_at')) ?? '',
  }
}

/**
 * `Organization.myRole` only models the roles that can exist inside an org;
 * `user_role` also carries platform-wide values (guest/host/support).
 */
function asMyRole(role: DbEnum<'user_role'> | undefined): Organization['myRole'] {
  return role === 'fleet_manager' || role === 'fleet_driver' || role === 'admin' ? role : null
}

// ─── Service ────────────────────────────────────────────────────────────────

const VALID_INSURANCE_MODELS = ['platform', 'fleet', 'hybrid', 'none'] as const
const MAX_ORGS_PER_USER = 5

/** Shape returned by the claimable-vehicles select in `findClaimableVehicles`. */
type ClaimableVehicleRow = Cols<'vehicles', 'id' | 'title' | 'detected_legal_name'>

class OrganizationServiceImpl {
  // ─── Auth Helpers ───────────────────────────────────────────────────────

  /** Assert the caller is a fleet_manager of the given org. Throws if not. */
  private async assertManager(orgId: string, userId: string): Promise<void> {
    const { data } = await supabase
      .from('organization_members')
      .select('role')
      .eq('organization_id', orgId)
      .eq('profile_id', userId)
      .eq('is_active', true)
      .not('accepted_at', 'is', null)
      .single()

    if (!data || (data.role !== 'fleet_manager' && data.role !== 'admin')) {
      throw ServiceError.forbidden('Only managers can perform this action')
    }
  }

  // ─── CRUD ───────────────────────────────────────────────────────────────

  /**
   * Create a new organization. The creator is automatically added as fleet_manager.
   * Rate-limited to MAX_ORGS_PER_USER per user.
   */
  async create(input: CreateOrganizationInput): Promise<Organization> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()

    const taxId = formatSvNit(input.taxId)
    if (!taxId) {
      throw new ServiceError({
        code: 'VALIDATION_ERROR',
        message: 'Organization NIT is required',
        userMessageKey: 'validation.org_tax_id_required',
        statusCode: 422,
      })
    }

    // Rate limit: max orgs per user
    const { count } = await supabase
      .from('organization_members')
      .select('id', { count: 'exact', head: true })
      .eq('profile_id', user.id)
      .eq('role', 'fleet_manager')
      .eq('is_active', true)
      .not('accepted_at', 'is', null)

    if ((count ?? 0) >= MAX_ORGS_PER_USER) {
      throw new ServiceError({
        message: `Maximum ${MAX_ORGS_PER_USER} organizations per user`,
        code: 'ORG_LIMIT_REACHED',
        statusCode: 429,
      })
    }

    // Validate insurance model
    const insuranceModel = input.insuranceModel ?? 'fleet'
    if (
      !VALID_INSURANCE_MODELS.includes(insuranceModel as (typeof VALID_INSURANCE_MODELS)[number])
    ) {
      throw ServiceError.badRequest('Invalid insurance model')
    }
    if (insuranceModel !== 'fleet') {
      throw new ServiceError({
        message: 'Por ahora, las agencias deben usar seguro propio de la flota.',
        code: 'EARLY_ACCESS_ORG_INSURANCE_LOCKED',
        statusCode: 403,
        userMessageKey: 'listing.validation.insurance_agency_only',
      })
    }

    const { data, error } = await supabase.rpc('create_organization_with_owner', {
      p_name: input.name,
      p_legal_name: normalizeLegalEntityName(input.legalName).trim(),
      p_tax_id: taxId,
      p_email: input.email,
      // Omitted, not null: both parameters are `DEFAULT NULL` in SQL, so leaving them out is
      // the same value by the function's own definition — and it matches the signature the
      // generated types describe.
      p_phone: input.phone ?? undefined,
      p_country_id: input.countryId ?? 'SV',
      p_city_id: input.cityId ?? undefined,
      p_insurance_model: insuranceModel,
    })

    if (error) {
      const message = error.message.toLowerCase()
      if (error.code === '22023') {
        const nitValidationKey = message.includes('14')
          ? 'validation.org_tax_id_format'
          : message.includes('nit') || message.includes('dígito') || message.includes('digito')
            ? 'validation.org_tax_id_checksum'
            : 'errors.validation'
        throw new ServiceError({
          message: error.message,
          code: 'VALIDATION_ERROR',
          statusCode: 422,
          userMessageKey: nitValidationKey,
          cause: error,
        })
      }
      if (error.code === '23505' || message.includes('organización activa con este nit')) {
        throw new ServiceError({
          message: error.message,
          code: 'ORG_TAX_ID_EXISTS',
          statusCode: 409,
          userMessageKey: 'org.tax_id_exists',
          cause: error,
        })
      }
      if (error.code === '54000') {
        throw new ServiceError({
          message: error.message,
          code: 'ORG_LIMIT_REACHED',
          statusCode: 429,
          userMessageKey: 'org.limit_reached',
          cause: error,
        })
      }
      if (error.code === '23503') {
        throw new ServiceError({
          message: error.message,
          code: 'ORG_RELATED_DATA_MISSING',
          statusCode: 422,
          userMessageKey: 'org.create_failed',
          cause: error,
        })
      }
      throw ServiceError.fromSupabaseError(error)
    }

    if (!data) throw ServiceError.notFound('Organization')

    return mapOrganization(data)
  }

  /**
   * Get organization by ID.
   */
  async getById(id: string): Promise<Organization> {
    const { data, error } = await supabase.from('organizations').select('*').eq('id', id).single()

    if (error) throw ServiceError.fromSupabaseError(error)
    return mapOrganization(data)
  }

  /**
   * List organizations the current user belongs to.
   */
  async getMyOrganizations(): Promise<Organization[]> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()

    // Claim deferred agency invites that were created before this profile
    // existed. This makes "invite employee by email/phone -> employee signs
    // up -> org appears" work without a separate inbox screen.
    const { error: claimError } = await supabase.rpc(
      'accept_pending_organization_invites_for_profile',
    )
    if (claimError) throw ServiceError.fromSupabaseError(claimError)

    const { data: memberships, error: memError } = await supabase
      .from('organization_members')
      .select('organization_id, role')
      .eq('profile_id', user.id)
      .eq('is_active', true)
      .not('accepted_at', 'is', null)

    if (memError) throw ServiceError.fromSupabaseError(memError)
    if (!memberships?.length) return []

    const roleByOrg = new Map<string, DbEnum<'user_role'>>()
    memberships.forEach((m) => roleByOrg.set(m.organization_id, m.role))
    const orgIds = Array.from(roleByOrg.keys())

    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .in('id', orgIds)
      .eq('is_active', true)

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map((row) => {
      const org = mapOrganization(row)
      org.myRole = asMyRole(roleByOrg.get(org.id))
      return org
    })
  }

  /**
   * Update organization details. Requires fleet_manager role.
   */
  /**
   * Upload a logo for the organization. Storage bucket: `org-logos` (public).
   * Writes the public URL back to `organizations.logo_url`.
   */
  async uploadLogo(orgId: string, imageUri: string): Promise<string> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()
    await this.assertManager(orgId, user.id)

    const compressed = await compressImage(imageUri, 'logo')
    const filePath = `${orgId}/logo.jpg`

    const { error: uploadError } = await uploadBytesWithSessionRecovery(
      'org-logos',
      filePath,
      compressed.bytes,
      { upsert: true, contentType: 'image/jpeg' },
    )

    if (uploadError) throw ServiceError.fromSupabaseError(uploadError)
    await assertObjectStored('org-logos', filePath)

    const { data: urlData } = supabase.storage.from('org-logos').getPublicUrl(filePath)
    const logoUrl = urlData.publicUrl

    const { error: updateError } = await supabase
      .from('organizations')
      .update({ logo_url: logoUrl })
      .eq('id', orgId)

    if (updateError) throw ServiceError.fromSupabaseError(updateError)

    return logoUrl
  }

  async update(id: string, input: UpdateOrganizationInput): Promise<Organization> {
    // Authorization: caller must be a manager of this org (defense-in-depth —
    // RLS also enforces, but we reject earlier with a clear error message).
    // This check prevents any authenticated user from editing any
    // org's metadata (name, email, address) by knowing the org id. Closed.
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()
    await this.assertManager(id, user.id)

    const updateData: UpdateRow<'organizations'> = {}
    if (input.name !== undefined) updateData.name = input.name
    if (input.legalName !== undefined)
      updateData.legal_name = normalizeLegalEntityName(input.legalName).trim()
    if (input.taxId !== undefined) updateData.tax_id = input.taxId
    if (input.email !== undefined) updateData.email = input.email
    if (input.phone !== undefined) updateData.phone = input.phone
    if (input.website !== undefined) updateData.website = input.website
    if (input.addressLine1 !== undefined) updateData.address_line1 = input.addressLine1
    if (input.addressLine2 !== undefined) updateData.address_line2 = input.addressLine2
    if (input.addressCity !== undefined) updateData.address_city = input.addressCity
    if (input.addressRegion !== undefined) updateData.address_region = input.addressRegion
    if (input.addressPostalCode !== undefined)
      updateData.address_postal_code = input.addressPostalCode
    if (input.insuranceModel !== undefined) {
      if (input.insuranceModel !== 'fleet') {
        throw new ServiceError({
          message: 'Por ahora, las agencias deben usar seguro propio de la flota.',
          code: 'EARLY_ACCESS_ORG_INSURANCE_LOCKED',
          statusCode: 403,
          userMessageKey: 'listing.validation.insurance_agency_only',
        })
      }
      updateData.insurance_model = input.insuranceModel
    }

    const { data, error } = await supabase
      .from('organizations')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) throw ServiceError.fromSupabaseError(error)
    return mapOrganization(data)
  }

  // ─── Members ────────────────────────────────────────────────────────────

  /**
   * ¿Pertenezco a esta organización, y puedo administrarla?
   *
   * PREGUNTARLE AL SERVIDOR, NO DEDUCIRLO DEL ROSTER. Las pantallas de
   * organización derivaban el permiso de la lista de miembros, que es
   * exactamente lo que un no-miembro no puede ver: RLS no da 403 ahí, FILTRA, y
   * devuelve cero filas sin error. La rama de rescate para "roster vacío"
   * --escrita para el caso legítimo de una organización recién creada-- se
   * encendía para cualquiera, y le regalaba el botón de invitar y su formulario.
   *
   * `is_org_member` / `is_org_manager` son las MISMAS funciones que usan las
   * políticas de RLS, así que esto no es una cuarta re-derivación del permiso:
   * es preguntarle temprano a quien ya decide. Y ninguna de las dos lanza
   * excepción: devuelven false y ya, así que preguntar no puede romper nada.
   */
  async getMembership(orgId: string): Promise<{ isMember: boolean; isManager: boolean }> {
    const [member, manager] = await Promise.all([
      supabase.rpc('is_org_member', { org_id: orgId }),
      supabase.rpc('is_org_manager', { org_id: orgId }),
    ])

    if (member.error) throw ServiceError.fromSupabaseError(member.error)
    if (manager.error) throw ServiceError.fromSupabaseError(manager.error)

    return { isMember: member.data === true, isManager: manager.data === true }
  }

  /**
   * List members of an organization with profile data.
   *
   * Reads through `org_member_profiles` on purpose: the embedded select over
   * `profiles(email, phone)` would die with a 42501 the day the profile
   * column grants close (anti-disintermediation, WS-C2). The RPC enforces
   * the same membership rule the table policy does.
   */
  async getMembers(orgId: string): Promise<OrganizationMember[]> {
    const { data, error } = await supabase.rpc('org_member_profiles', {
      p_organization_id: orgId,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!Array.isArray(data)) return []
    return data.map(mapMemberFromJson)
  }

  /**
   * List deferred invites for people who do not have a Tago account yet.
   * Only managers can read this through the RPC.
   */
  async getPendingInvites(orgId: string): Promise<OrganizationPendingInvite[]> {
    const { data, error } = await supabase.rpc('list_organization_pending_invites', {
      p_organization_id: orgId,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!Array.isArray(data)) return []
    return data.map(mapPendingInvite)
  }

  /**
   * Invite a member by email or phone. Creates a pending record.
   * Requires fleet_manager role (defense-in-depth — RLS also enforces).
   */
  async inviteMember(input: InviteMemberInput): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()

    // Business rule: only the legal representative is fleet_manager. The RPC
    // hardcodes fleet_driver and handles both cases:
    // - existing Tago profile: membership is added immediately
    // - no profile yet: a deferred invite is stored and claimed after signup
    const { error } = await supabase.rpc('create_organization_member_invite', {
      p_organization_id: input.organizationId,
      p_email: input.email ?? undefined,
      p_phone: input.phone ?? undefined,
    })

    if (error) {
      if (error.code === '22023') {
        throw new ServiceError({
          message: error.message,
          code: 'VALIDATION_ERROR',
          statusCode: 422,
          userMessageKey: 'validation.email_or_phone_required',
          cause: error,
        })
      }
      if (error.code === '23505') {
        throw new ServiceError({
          message: error.message,
          code: 'ALREADY_INVITED',
          statusCode: 409,
          userMessageKey: 'org.invite_already_exists',
          cause: error,
        })
      }
      throw ServiceError.fromSupabaseError(error)
    }
  }

  /**
   * Accept a pending invitation. Validates caller owns the invite.
   */
  async acceptInvite(memberId: string): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()

    // Verify caller owns this invite
    const { data: member } = await supabase
      .from('organization_members')
      .select('profile_id, accepted_at')
      .eq('id', memberId)
      .single()

    if (!member) throw ServiceError.notFound('Invitation')
    if (member.profile_id !== user.id) throw ServiceError.forbidden('Not your invitation')
    if (member.accepted_at) return // idempotent — already accepted

    const { error } = await supabase
      .from('organization_members')
      .update({ accepted_at: new Date().toISOString() })
      .eq('id', memberId)

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Remove a member (soft-delete). Prevents removal of last manager.
   */
  async removeMember(memberId: string): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()

    // Get the member being removed to check their org and role
    const { data: member } = await supabase
      .from('organization_members')
      .select('organization_id, role')
      .eq('id', memberId)
      .single()

    if (!member) throw ServiceError.notFound('Member')

    await this.assertManager(member.organization_id, user.id)

    // Prevent removing the last manager
    if (member.role === 'fleet_manager') {
      const { count } = await supabase
        .from('organization_members')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', member.organization_id)
        .eq('role', 'fleet_manager')
        .eq('is_active', true)
        .not('accepted_at', 'is', null)

      if ((count ?? 0) <= 1) {
        throw new ServiceError({
          message: 'Cannot remove the last manager',
          code: 'LAST_MANAGER',
          statusCode: 400,
        })
      }
    }

    const { error } = await supabase
      .from('organization_members')
      .update({ is_active: false })
      .eq('id', memberId)

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Update a member's role.
   *
   * Authorization: caller must be a manager of the member's org. Without
   * this check, any authenticated user knowing a member_id
   * could promote themselves (or anyone) to fleet_manager of any org. Closed.
   */
  async updateMemberRole(memberId: string, role: 'fleet_manager' | 'fleet_driver'): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()

    // Fetch the member's org so we can assert the caller is a manager OF
    // THAT org (not just any org).
    const { data: member, error: memberErr } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('id', memberId)
      .single()

    if (memberErr) throw ServiceError.fromSupabaseError(memberErr)
    if (!member) throw ServiceError.notFound('Member')

    await this.assertManager(member.organization_id, user.id)

    const { error } = await supabase
      .from('organization_members')
      .update({ role })
      .eq('id', memberId)

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  // ─── Vehicle Claiming ─────────────────────────────────────────────────

  /**
   * Claim a vehicle for the organization. Sets vehicle.organization_id.
   * Requires manager role + legal name match validation.
   */
  async claimVehicle(orgId: string, vehicleId: string): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()

    // Authorization: must be a manager
    await this.assertManager(orgId, user.id)

    // Verify vehicle exists and has no org assigned
    const { data: vehicle } = await supabase
      .from('vehicles')
      .select('id, organization_id')
      .eq('id', vehicleId)
      .single()

    if (!vehicle) throw ServiceError.notFound('Vehicle')
    if (vehicle.organization_id) {
      throw new ServiceError({
        message: 'Vehicle already belongs to an organization',
        code: 'VEHICLE_ALREADY_CLAIMED',
        statusCode: 409,
      })
    }

    const { error } = await supabase
      .from('vehicles')
      .update({ organization_id: orgId })
      .eq('id', vehicleId)

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Release a vehicle from the organization. Sets organization_id to null.
   *
   * Authorization: caller must be a manager of the vehicle's CURRENT
   * organization. Without this check, any user could
   * detach any vehicle from any org by knowing a vehicle_id. Closed.
   */
  async releaseVehicle(vehicleId: string): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()

    // Fetch the vehicle's current org so we can assert the caller is a
    // manager of it. If the vehicle has no org, there's nothing to release.
    const { data: vehicle, error: vehicleErr } = await supabase
      .from('vehicles')
      .select('id, organization_id')
      .eq('id', vehicleId)
      .single()

    if (vehicleErr) throw ServiceError.fromSupabaseError(vehicleErr)
    if (!vehicle) throw ServiceError.notFound('Vehicle')
    if (!vehicle.organization_id) {
      throw new ServiceError({
        message: 'Vehicle is not assigned to any organization',
        code: 'VEHICLE_NOT_CLAIMED',
        statusCode: 400,
      })
    }

    await this.assertManager(vehicle.organization_id, user.id)

    const { error } = await supabase
      .from('vehicles')
      .update({ organization_id: null })
      .eq('id', vehicleId)

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Find claimable vehicles: vehicles with company_owner_detected=true
   * whose detected_legal_name fuzzy-matches the org's legal name.
   */
  async findClaimableVehicles(
    orgId: string,
  ): Promise<Array<{ id: string; title: string; detectedLegalName: string }>> {
    const org = await this.getById(orgId)
    if (!org.legalName) return []

    // `company_owner_detected` / `detected_legal_name` come from migration
    // 20260410 and are part of the generated schema, so the select can ask
    // for the column the filter below reads instead of casting it in.
    const { data, error } = await supabase
      .from('vehicles')
      .select('id, title, detected_legal_name')
      .eq('company_owner_detected', true)
      .is('organization_id', null)

    if (error) throw ServiceError.fromSupabaseError(error)

    const rows: ClaimableVehicleRow[] = data ?? []

    const normalize = (s: string) =>
      s
        .toUpperCase()
        .replace(/[.,\s]+/g, ' ')
        .trim()
    const orgNorm = normalize(org.legalName)

    return rows
      .filter((v) => {
        if (!v.detected_legal_name) return false
        const vNorm = normalize(v.detected_legal_name)
        return vNorm.includes(orgNorm) || orgNorm.includes(vNorm)
      })
      .map((v) => ({
        id: v.id,
        title: v.title,
        detectedLegalName: v.detected_legal_name ?? '',
      }))
  }

  /**
   * Search for existing organizations by legal name (fuzzy).
   */
  async findByLegalName(legalName: string): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .ilike('legal_name', `%${legalName.replace(/[%_\\]/g, '\\$&')}%`)
      .eq('is_active', true)
      .limit(5)

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapOrganization)
  }
  /**
   * Request to join an organization. Creates a pending membership (accepted_at = null).
   * The org manager must approve by setting accepted_at.
   */
  async requestToJoin(orgId: string): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw ServiceError.unauthorized()

    // Check not already a member
    const { data: existing } = await supabase
      .from('organization_members')
      .select('id, is_active')
      .eq('organization_id', orgId)
      .eq('profile_id', user.id)
      .maybeSingle()

    if (existing) {
      throw new ServiceError({
        message: 'Already a member or pending',
        code: 'ALREADY_MEMBER',
        statusCode: 409,
      })
    }

    const { error } = await supabase.from('organization_members').insert({
      organization_id: orgId,
      profile_id: user.id,
      role: 'fleet_driver',
      // accepted_at is NULL -- pending manager approval
    })

    if (error) throw ServiceError.fromSupabaseError(error)
  }
}

export const organizationService = new OrganizationServiceImpl()
